import QuestionMark from "@husky-hrc/Components/Icons/QuestionMark";
import ExtendedApplicationState from "@husky-hrc/Framework/Store/ExtendedApplicationState";
import { ProductConfigModel, StepModel } from "@husky-hrc/Framework/Types/ApiModel";
import ProductAddToCartButton from "@husky-hrc/Overrides/Components/ProductAddToCartButton";
import Constants from "@husky-hrc/Utilities/Constants";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContext, ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import { getProductState } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import Icon, { IconProps } from "@insite/mobius/Icon";
import ChevronLeft from "@insite/mobius/Icons/ChevronLeft";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import Modal from "@insite/mobius/Modal/Modal";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { connect } from "react-redux";
import styled, { css } from "styled-components";

interface CMSProps {
    priceText: "string";
    leadTimeText: "string";
    estimatedLeadTime: "string";
    rfqText: "string";
}

interface OwnProps {
    productId?: string;
    configuration?: ProductConfigModel;
    selectedStep: StepModel;
    cmsProps: CMSProps;
    onStepChange: (step: string, direction: "Forward" | "Backward") => void;
    validator: () => boolean;
    isLoading: boolean;
    onAddingToCart: (value: boolean) => void;
    preventGoingNextStep: boolean;
}

const mapStateToProps = (state: ExtendedApplicationState, props: OwnProps) => {
    const product = getProductState(state, props.productId);
    const currentCurrency = state.context.session.currency;
    return {
        product,
        currentCurrency,
        productInfo: state.productConfig.selectedProductInfo,
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface TactonConfigFooterStyles {
    containerStyles?: InjectableCss;
    textContainerStyles?: InjectableCss;
    textStyles?: TypographyPresentationProps;
    subTextStyles?: TypographyPresentationProps;
    rfqTextStyles?: TypographyPresentationProps;
    rfqIconStyles?: IconProps;
    mobileBackButtonStyles?: ButtonPresentationProps;
    mobileNextButtonStyles?: ButtonPresentationProps;
    nextButtonStyles?: ButtonPresentationProps;
    hiddenButtonStyles?: HiddenProps;
    mobileButtonsContainerStyles?: InjectableCss;
    mobileButtonStyles?: InjectableCss;
    mobileButtonTextStyles?: InjectableCss;
}

export const tactonConfigFooterStyles: TactonConfigFooterStyles = {
    containerStyles: {
        css: css`
            position: sticky;
            bottom: 0;
            width: 100%;

            display: flex;
            z-index: 100;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            height: 56px;
                        `,
                        null,
                        css`
                            height: 64px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    textContainerStyles: {
        css: css`
            background: #3d3e3f;
            color: ${getColor("common.background")};
            border-right: 1px solid rgba(255, 255, 255, 0.3);
            display: flex;
            align-items: center;
            justify-content: center;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            flex-wrap: wrap;
                            padding: 6px;
                            gap: 3px;
                            flex-grow: 1;
                        `,
                        null,
                        css`
                            flex-wrap: wrap-reverse;
                            padding: 0 40px;
                            gap: 12px;
                        `,
                        css`
                            flex-grow: 0;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    textStyles: {
        css: css`
            font-weight: bold;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            font-size: 16px;
                            letter-spacing: 0;
                            line-height: normal;
                        `,
                        null,
                        css`
                            letter-spacing: 1px;
                            font-size: 26px;
                            line-height: 32px;
                            color: ${getColor("common.background")};
                        `,
                    ],
                    "min",
                )}
        `,
    },
    subTextStyles: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            font-size: 16px;
                            line-height: normal;
                            letter-spacing: 0.1px;
                        `,
                        null,
                        css`
                            font-size: 14px;
                            line-height: 22px;
                            letter-spacing: 0.2px;
                            color: #b6b9bb;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    rfqTextStyles: {
        color: "common.background",
        css: css`
            background: ${getColor("danger.main")};
            display: flex;
            align-items: center;
            gap: 5px;
            svg {
                cursor: pointer;
            }
            font-weight: bold;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            flex-grow: 1;
                            padding: 0 25px;
                            font-size: 18px;
                            line-height: 24px;
                            letter-spacing: 0.2px;
                        `,
                        null,
                        null,
                        css`
                            font-size: 26px;
                            letter-spacing: 1px;
                            line-height: 32px;
                            flex-grow: 0;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    rfqIconStyles: {
        src: QuestionMark,
        color: "common.background",
        size: 25,
        css: css`
            &:focus {
                outline: 2px solid #09f;
            }
        `,
    },
    mobileNextButtonStyles: {
        variant: "primary",
        css: css`
            font-weight: bold;
            padding: 2em 4em;
            &:before {
                content: "";
                z-index: 1;
                display: block;
                position: absolute;
                left: -10px;
                width: 17px;
                height: 108%;
                transform: skew(-10deg);
                top: -2px;
                bottom: 0;
                transition: transform 0.25s ease-in-out, background 0.25s ease-in-out;
                background: white !important;
            }
        `,
    },
    mobileBackButtonStyles: {
        variant: "tertiary",
        css: css`
            position: relative;
            font-weight: bold;
            padding: 2em 4em;
            text-transform: capitalize;
            &:disabled {
                color: ${getColor("text.main")};
            }
            :disabled svg {
                stroke: ${getColor("text.main")};
            }
            &:after {
                content: "";
                z-index: 1;
                display: block;
                position: absolute;
                right: -10px;
                width: 17px;
                height: 108%;
                transform: skew(-10deg);
                top: -2px;
                bottom: 0;
                transition: transform 0.25s ease-in-out, background 0.25s ease-in-out;
                background: white !important;
            }
        `,
    },
    nextButtonStyles: {
        variant: "primary",
        css: css`
            color: ${getColor("text.main")};
            font-weight: 600;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            height: 64px;
                            width: 152px;
                            border-radius: 4px;
                            span {
                                color: #000000 !important;
                            }
                        `,
                    ],
                    "max",
                )}
            &:disabled {
                color: ${getColor("text.disabled")};
            }
        `,
    },
    hiddenButtonStyles: {
        css: css`
            flex-grow: 1;
        `,
    },
    mobileButtonsContainerStyles: {
        css: css`
            display: flex;
            justify-content: center;
            padding-bottom: 28px;
        `,
    },
    mobileButtonStyles: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 16px;
            letter-spacing: 0.3px;
            line-height: 19px;
        `,
    },
    mobileButtonTextStyles: {
        css: css`
            margin: 0 8px;
            font-weight: bold;
        `,
    },
};

const ButtonContainer = styled.div<{ RFQ: boolean }>`
    flex-grow: 1;
    height: 100%;
    display: flex;
    justify-content: flex-end;
    background: ${props => (props.RFQ ? getColor("danger.main") : "#3d3e3f")};
    > button {
        min-width: 170px;
        font-size: 16px;
        line-height: 19px;
        letter-spacing: 0.27px;
        border: none;
        border-radius: 0;
    }
`;

const BackButton = styled.button<{ RFQ: boolean }>`
    position: relative;
    cursor: pointer;
    background: ${props => (props.RFQ ? getColor("danger.main") : "#3d3e3f")};
    color: ${props => (props.RFQ ? getColor("common.background") : "#b6b9bb")};
    font-weight: 600;
    text-transform: capitalize;
    > div,
    > div > div {
        display: flex;
        align-items: center;
        justify-content: center;
    }
    svg {
        stroke: ${props => (props.RFQ ? getColor("common.background") : "#b6b9bb")};
        height: 20px;
        width: 14px;
    }
    &:hover {
        color: ${getColor("common.background")};
        background: ${props => (props.RFQ ? getColor("danger.main") : "#3d3e3f")};
        svg {
            stroke: ${getColor("common.background")};
        }
        &:after {
            background: ${props => (props.RFQ ? getColor("danger.main") : "#3d3e3f")};
        }
    }
    &:active {
        > div > div {
            border-bottom: 1px solid rgba(255, 255, 255, 0.5);
        }
    }
    &:focus {
        outline: none;
        > div > div {
            box-shadow: 0 0 3px 3px #74a4f8;
        }
    }
    &:after {
        content: "";
        z-index: 1;
        display: block;
        position: absolute;
        right: -13px;
        width: 25px;
        height: auto;
        transform: skew(-10deg);
        top: 0;
        bottom: 0;
        transition: transform 0.25s ease-in-out, background 0.25s ease-in-out;
        background: ${props => (props.RFQ ? getColor("danger.main") : "#3d3e3f")};
    }
`;

const styles = tactonConfigFooterStyles;

const TactonConfigFooter: React.FC<Props> = ({
    product,
    productInfo,
    configuration,
    selectedStep,
    cmsProps,
    onStepChange,
    validator,
    onAddingToCart,
    isLoading,
    currentCurrency,
    preventGoingNextStep,
}) => {
    const [showModal, setShowModal] = React.useState(false);

    if (!product.value || !productInfo) {
        return null;
    }
    const productContext: ProductContextModel = {
        product: product.value,
        productInfo,
    };

    const handleStepClick = (stepDirection: "Forward" | "Backward") => {
        let index = configuration!.steps.indexOf(selectedStep);
        stepDirection === "Forward" ? index++ : index--;
        if (index >= 0 && index < configuration!.steps.length) {
            const step = configuration!.steps[index];
            onStepChange(step.name, stepDirection);
        }
    };

    const onFirstStep = selectedStep === configuration!.steps[0];

    const onLastStep = selectedStep === configuration!.steps.slice(-1).pop();

    return (
        <ProductContext.Provider value={productContext}>
            <Hidden above="md">
                <StyledWrapper {...styles.mobileButtonsContainerStyles}>
                    <Button
                        {...styles.mobileBackButtonStyles}
                        onClick={() => handleStepClick("Backward")}
                        disabled={isLoading || onFirstStep}
                    >
                        <StyledWrapper {...styles.mobileButtonStyles}>
                            <ButtonIcon src={ChevronLeft} size={19} />
                            <Typography {...styles.mobileButtonTextStyles}>{translate("Back")}</Typography>
                        </StyledWrapper>
                    </Button>

                    {onLastStep ? (
                        <ProductAddToCartButton
                            extendedStyles={styles.mobileNextButtonStyles}
                            data-test-selector="addToCartBtn"
                            configId={configuration?.id}
                            validator={validator}
                            disabledOverride={isLoading}
                            inProgress={onAddingToCart}
                        />
                    ) : (
                        <Button
                            {...styles.mobileNextButtonStyles}
                            onClick={() => handleStepClick("Forward")}
                            disabled={preventGoingNextStep || isLoading}
                        >
                            <StyledWrapper {...styles.mobileButtonStyles}>
                                <Typography {...styles.mobileButtonTextStyles}>{translate("Next")}</Typography>
                                <ButtonIcon src={ChevronRight} size={19} />
                            </StyledWrapper>
                        </Button>
                    )}
                </StyledWrapper>
            </Hidden>
            <StyledWrapper {...styles.containerStyles} data-id="tactonConfigFooter">
                {!configuration?.isRFQ ? (
                    <>
                        <StyledWrapper aria-live="polite" aria-atomic="true" {...styles.textContainerStyles}>
                            <Typography {...styles.textStyles}>{configuration?.pricing.netPrice}</Typography>
                            <Typography {...styles.subTextStyles}>{currentCurrency?.currencyCode}</Typography>
                            <Typography {...styles.subTextStyles}>{cmsProps.priceText}</Typography>
                        </StyledWrapper>
                        <StyledWrapper {...styles.textContainerStyles}>
                            <Typography {...styles.textStyles}>{cmsProps.estimatedLeadTime}</Typography>
                            <Typography {...styles.subTextStyles}>{cmsProps.leadTimeText}</Typography>
                        </StyledWrapper>
                    </>
                ) : (
                    <Typography role="alert" {...styles.rfqTextStyles}>
                        {cmsProps.rfqText}
                        <Icon
                            {...styles.rfqIconStyles}
                            tabIndex={0}
                            aria-label={translate("More info")}
                            role="button"
                            onClick={() => setShowModal(true)}
                        />
                    </Typography>
                )}
                <Hidden below="lg" {...styles.hiddenButtonStyles}>
                    <ButtonContainer RFQ={configuration!.isRFQ}>
                        {!onFirstStep && (
                            <BackButton
                                RFQ={configuration!.isRFQ}
                                onClick={() => handleStepClick("Backward")}
                                disabled={isLoading}
                            >
                                <StyledWrapper>
                                    <StyledWrapper>
                                        <ButtonIcon src={ChevronLeft} />
                                        <Typography>{translate("Back")}</Typography>
                                    </StyledWrapper>
                                </StyledWrapper>
                            </BackButton>
                        )}
                        {onLastStep ? (
                            <ProductAddToCartButton
                                extendedStyles={styles.nextButtonStyles}
                                data-test-selector="addToCartBtn"
                                configId={configuration?.id}
                                validator={validator}
                                disabledOverride={isLoading}
                                inProgress={onAddingToCart}
                            ></ProductAddToCartButton>
                        ) : (
                            <Button
                                {...styles.nextButtonStyles}
                                onClick={() => handleStepClick("Forward")}
                                disabled={preventGoingNextStep || isLoading}
                            >
                                <Typography>{translate("Next")}</Typography>
                            </Button>
                        )}
                    </ButtonContainer>
                </Hidden>
            </StyledWrapper>
            <Modal
                {...Constants.Styles.modalStyles.modal}
                headline={translate("Request For Quote Details")}
                isOpen={showModal}
                handleClose={() => setShowModal(false)}
            >
                {configuration?.rfqReason}
            </Modal>
        </ProductContext.Provider>
    );
};
export default connect(mapStateToProps)(TactonConfigFooter);
